import { useTranslation } from 'react-i18next';
import { MapPin, Utensils, Landmark, Waves, Mountain, Coffee } from 'lucide-react';

const AreaGuide = () => {
  const { t } = useTranslation();

  const thessalonikiGuide = {
    attractions: [
      { name: 'White Tower', description: 'Iconic Byzantine monument and symbol of Thessaloniki', icon: Landmark },
      { name: 'Aristotelous Square', description: 'Central square with cafes, shops, and sea views', icon: Coffee },
      { name: 'Ano Poli (Upper Town)', description: 'Historic neighborhood with traditional architecture', icon: Mountain },
      { name: 'Rotunda', description: 'Ancient Roman monument with stunning mosaics', icon: Landmark },
    ],
    restaurants: [
      { name: 'Extravaganza', description: 'Modern Mediterranean cuisine with creative dishes' },
      { name: 'Ergon Agora', description: 'Local produce market and restaurant' },
      { name: 'Mourga', description: 'Traditional Greek taverna with authentic flavors' },
      { name: 'Sempriko', description: 'Cozy spot for mezze and local wine' },
    ],
    beaches: [
      { name: 'Peraia Beach', description: '15km from city center, organized beach with cafes' },
      { name: 'Agia Triada Beach', description: 'Popular urban beach with easy access' },
      { name: 'Epanomi Beach', description: '25km away, quieter and more relaxed atmosphere' },
    ]
  };

  const halkidikiGuide = {
    beaches: [
      { name: 'Afytos Beach', description: 'Beautiful sandy beach with crystal clear waters' },
      { name: 'Kallithea Beach', description: 'Organized beach with water sports and beach bars' },
      { name: 'Possidi Beach', description: 'Long sandy beach with shallow waters, family-friendly' },
      { name: 'Sani Beach', description: 'Luxury resort area with pristine beaches' },
    ],
    villages: [
      { name: 'Afytos Village', description: 'Traditional stone-built village with cobblestone streets' },
      { name: 'Pefkohori', description: 'Lively resort town with nightlife and restaurants' },
      { name: 'Nikiti', description: 'Charming village with old town and modern marina' },
    ],
    activities: [
      { name: 'Water Sports', description: 'Jet skiing, parasailing, and paddleboarding' },
      { name: 'Boat Tours', description: 'Day trips to Mount Athos and secluded beaches' },
      { name: 'Hiking', description: 'Nature trails through pine forests and coastal paths' },
      { name: 'Diving', description: 'Explore underwater caves and marine life' },
    ]
  };

  return (
    <div className="bg-stone-50 min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-sans font-semibold mb-4">
            {t('nav.areaGuide')}
          </h1>
          <p className="text-lg text-stone-600 font-light max-w-2xl mx-auto">
            Discover the best of Thessaloniki and Halkidiki with our local recommendations
          </p>
        </div>

        {/* Thessaloniki Section */}
        <section className="mb-16">
          <div className="mb-8">
            <h2 className="text-4xl font-sans font-semibold mb-4 flex items-center">
              <MapPin className="w-8 h-8 text-primary mr-3" />
              Thessaloniki
            </h2>
            <p className="text-lg text-stone-600 font-light">
              Greece's second-largest city combines Byzantine heritage, vibrant culture, and seaside charm.
            </p>
          </div>

          {/* Hero Image */}
          <div className="mb-12 aspect-[21/9] rounded-sm overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1766261010715-f5c230be72f7?w=1600"
              alt="Thessaloniki White Tower"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Attractions */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <div className="flex items-center mb-4">
                <Landmark className="w-6 h-6 text-primary mr-2" />
                <h3 className="text-xl font-sans font-semibold">Top Attractions</h3>
              </div>
              <ul className="space-y-3">
                {thessalonikiGuide.attractions.map((item, index) => (
                  <li key={index}>
                    <p className="font-semibold text-stone-900">{item.name}</p>
                    <p className="text-sm text-stone-600 font-light">{item.description}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Restaurants */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <div className="flex items-center mb-4">
                <Utensils className="w-6 h-6 text-primary mr-2" />
                <h3 className="text-xl font-sans font-semibold">Where to Eat</h3>
              </div>
              <ul className="space-y-3">
                {thessalonikiGuide.restaurants.map((item, index) => (
                  <li key={index}>
                    <p className="font-semibold text-stone-900">{item.name}</p>
                    <p className="text-sm text-stone-600 font-light">{item.description}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Beaches */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <div className="flex items-center mb-4">
                <Waves className="w-6 h-6 text-primary mr-2" />
                <h3 className="text-xl font-sans font-semibold">Nearby Beaches</h3>
              </div>
              <ul className="space-y-3">
                {thessalonikiGuide.beaches.map((item, index) => (
                  <li key={index}>
                    <p className="font-semibold text-stone-900">{item.name}</p>
                    <p className="text-sm text-stone-600 font-light">{item.description}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Halkidiki Section */}
        <section>
          <div className="mb-8">
            <h2 className="text-4xl font-sans font-semibold mb-4 flex items-center">
              <Waves className="w-8 h-8 text-primary mr-3" />
              Halkidiki
            </h2>
            <p className="text-lg text-stone-600 font-light">
              Famous for its three peninsulas, Halkidiki offers pristine beaches, turquoise waters, and pine forests.
            </p>
          </div>

          {/* Hero Image */}
          <div className="mb-12 aspect-[21/9] rounded-sm overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1631260513693-c070bd854877?w=1600"
              alt="Halkidiki Beach"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Beaches */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <div className="flex items-center mb-4">
                <Waves className="w-6 h-6 text-primary mr-2" />
                <h3 className="text-xl font-sans font-semibold">Best Beaches</h3>
              </div>
              <ul className="space-y-3">
                {halkidikiGuide.beaches.map((item, index) => (
                  <li key={index}>
                    <p className="font-semibold text-stone-900">{item.name}</p>
                    <p className="text-sm text-stone-600 font-light">{item.description}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Villages */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <div className="flex items-center mb-4">
                <Mountain className="w-6 h-6 text-primary mr-2" />
                <h3 className="text-xl font-sans font-semibold">Traditional Villages</h3>
              </div>
              <ul className="space-y-3">
                {halkidikiGuide.villages.map((item, index) => (
                  <li key={index}>
                    <p className="font-semibold text-stone-900">{item.name}</p>
                    <p className="text-sm text-stone-600 font-light">{item.description}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Activities */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <div className="flex items-center mb-4">
                <Coffee className="w-6 h-6 text-primary mr-2" />
                <h3 className="text-xl font-sans font-semibold">Activities</h3>
              </div>
              <ul className="space-y-3">
                {halkidikiGuide.activities.map((item, index) => (
                  <li key={index}>
                    <p className="font-semibold text-stone-900">{item.name}</p>
                    <p className="text-sm text-stone-600 font-light">{item.description}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section className="mt-16">
          <div className="bg-primary text-white p-8 rounded-sm">
            <h2 className="text-3xl font-sans font-semibold mb-6">Local Tips</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-2">🚖 Getting Around</h3>
                <p className="text-white/90 font-light text-sm">
                  Rent a car to explore Halkidiki freely. Thessaloniki is walkable, but taxis and buses are readily available.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">🍔 Best Time to Visit</h3>
                <p className="text-white/90 font-light text-sm">
                  May-June and September-October offer perfect weather with fewer crowds. Peak season is July-August.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">💶 Currency & Cards</h3>
                <p className="text-white/90 font-light text-sm">
                  Euro (EUR) is the currency. Cards are widely accepted, but carry some cash for small tavernas and beaches.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">🗣️ Language</h3>
                <p className="text-white/90 font-light text-sm">
                  Greek is the official language, but English is spoken in tourist areas and by younger generations.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AreaGuide;
